import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import axiosInstance from "../api/axiosInstance";

export default function MrrLineChart({ months = 18, teamId, planId, excludeCurrentMonth = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // fetch the same aggregate you use for the stacked chart
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { months };
        if (teamId != null) params.team_id = teamId;
        if (planId != null) params.plan_id = planId;
        if (excludeCurrentMonth) params.exclude_current_month = 1;

        const res = await axiosInstance.get("/api/mrr/cohorts", { params });
        if (!cancelled) {
          const data = Array.isArray(res.data?.data) ? res.data.data : [];
          setRows(data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load MRR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months, teamId, planId, excludeCurrentMonth]);

  const { categories, seriesData, usesTotal } = useMemo(() => {
    const categories = rows.map(r => r.month);

    // Prefer total_mrr if backend provides it; otherwise fall back to net change line
    const hasTotal = rows.length && typeof rows[0]?.total_mrr === "number";
    const seriesData = hasTotal
      ? rows.map(r => Number(r.total_mrr || 0))
      : rows.map(r => Number(r.net_mrr || 0));

    return { categories, seriesData, usesTotal: hasTotal };
  }, [rows]);

  const options = useMemo(() => ({
    chart: {
      type: "line",
      height: 360,
      style: { fontFamily: "Montserrat, system-ui, sans-serif" }
    },
    title: { text: usesTotal ? "Total MRR Over Time" : "Net MRR Change per Month" },
    credits: { enabled: false },
    legend: {enabled: false },
    xAxis: { categories },
    yAxis: {
      title: { text: usesTotal ? "MRR (USD)" : "Net Change (USD)" },
      allowDecimals: false
    },
    tooltip: {
      shared: false,
      pointFormatter: function () {
        const fmt = new Intl.NumberFormat(undefined, {
          style: "currency", currency: "USD", maximumFractionDigits: 0, currencyDisplay: "narrowSymbol"
        });
        return `<span>${fmt.format(this.y)}</span>`;
      },
      useHTML: true
    },
    series: [
      {
        name: usesTotal ? "Total MRR" : "Net MRR Δ",
        data: seriesData,
        color: "#28277e",
        marker: { enabled: true }
      }
    ]
  }), [categories, seriesData, usesTotal]);

  if (loading) return <div>Loading…</div>;
  if (error) return <div>{error}</div>;
  if (!rows.length) return <div>No data</div>;

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
